import React, { useState } from "react";
import { ArrowUpRight, Star } from "lucide-react";
import { VIRTUAL_BRANDS, DUM_LOGO_URL } from "../mock/data";

const BrandCard = ({ brand, large, hovered, setHovered }) => {
  const isHovered = hovered === brand.id;
  return (
    <div
      id={`brand-${brand.id}`}
      onMouseEnter={() => setHovered(brand.id)}
      onMouseLeave={() => setHovered(null)}
      className={`group relative overflow-hidden rounded-[28px] border border-white/10 cursor-pointer transition-all duration-500 hover:border-[#14E0BF]/40 ${
        large ? "lg:col-span-2 lg:row-span-2 min-h-[560px]" : "min-h-[280px]"
      }`}
      style={{ backgroundColor: brand.bg }}
    >
      {/* Image */}
      <div className="absolute inset-0">
        <img
          src={brand.image}
          alt={brand.name}
          className={`w-full h-full object-cover transition-all duration-700 ${
            isHovered ? "scale-110 opacity-90" : "scale-100 opacity-70"
          }`}
        />
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(180deg, transparent 30%, ${brand.bg} 100%)`,
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 p-6 lg:p-8 h-full flex flex-col justify-between">
        <div className="flex items-start justify-between">
          <div>
            {brand.isFeatured && (
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-3 py-1">
                  <Star className="w-3 h-3 fill-[#F77F00] text-[#F77F00]" />
                  <span className="font-mono text-[10px] uppercase tracking-wider text-white">
                    Flagship Brand
                  </span>
                </div>
              </div>
            )}
            <span
              className="inline-block font-mono text-[11px] uppercase tracking-[0.18em] mb-2"
              style={{ color: brand.accent }}
            >
              {brand.cuisine}
            </span>
          </div>
          {brand.isFeatured ? (
            <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl overflow-hidden bg-white border-2 border-white/20 shrink-0">
              <img src={DUM_LOGO_URL} alt={brand.name} className="w-full h-full object-cover" />
            </div>
          ) : (
            <ArrowUpRight
              className="w-5 h-5 transition-transform duration-300 group-hover:rotate-45"
              style={{ color: brand.accent }}
            />
          )}
        </div>

        <div>
          <h3
            className={`font-display uppercase leading-[0.95] mb-3 ${
              large ? "text-5xl lg:text-7xl" : "text-3xl lg:text-4xl"
            } text-white`}
          >
            {brand.name}
          </h3>
          <p
            className={`font-serif-display italic mb-4 ${
              large ? "text-2xl lg:text-3xl" : "text-lg lg:text-xl"
            } text-white/85`}
          >
            {brand.tagline}
          </p>

          {large && (
            <p className="text-white/65 text-base lg:text-lg leading-relaxed mb-5 max-w-[560px]">
              {brand.description}
            </p>
          )}

          {large && (
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {brand.bestSellers.map((b) => (
                <span
                  key={b}
                  className="font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full bg-white/8 border border-white/10 text-white/80"
                >
                  {b}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const VirtualBrandsBento = () => {
  const [hovered, setHovered] = useState(null);
  const featured = VIRTUAL_BRANDS.find((b) => b.isFeatured);
  const others = VIRTUAL_BRANDS.filter((b) => !b.isFeatured);

  return (
    <section id="brands" className="relative py-24 lg:py-32 bg-[#07070A] overflow-hidden">
      <div className="absolute top-1/4 -right-32 w-[400px] h-[400px] rounded-full bg-[#14E0BF]/8 blur-[120px]" />

      <div className="relative max-w-[1480px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-[720px]">
            <div className="inline-flex items-center gap-2 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#14E0BF] animate-pulse-dot" />
              <span className="font-mono text-[11px] text-[#14E0BF] uppercase tracking-[0.2em]">
                The Portfolio
              </span>
            </div>
            <h2 className="text-white">
              <span className="font-display text-5xl lg:text-7xl leading-[0.95] block">
                Four Indian Brands.
              </span>
              <span className="font-serif-display italic text-5xl lg:text-7xl leading-[0.95] block text-[#14E0BF] mt-1">
                One smart kitchen.
              </span>
            </h2>
          </div>
          <p className="text-white/65 text-base lg:text-lg max-w-[420px]">
            Each brand is engineered around a different occasion – weeknight comfort, weekend feast, late-night street – so they sell side by side, not against each other.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:auto-rows-[280px]">
          {featured && (
            <BrandCard brand={featured} large hovered={hovered} setHovered={setHovered} />
          )}
          {others.map((b) => (
            <BrandCard key={b.id} brand={b} hovered={hovered} setHovered={setHovered} />
          ))}
        </div>

        {/* Bottom note */}
        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 px-6 py-5 rounded-2xl bg-[#14141B] border border-white/8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#14E0BF]/15 border border-[#14E0BF]/30 flex items-center justify-center">
              <Star className="w-4 h-4 text-[#14E0BF]" />
            </div>
            <p className="text-white/70 text-sm lg:text-base">
              Most partners launch with <span className="text-white font-semibold">two brands</span> and unlock the next as they hit sales targets.
            </p>
          </div>
          <a
            href="#process"
            className="font-mono text-xs uppercase tracking-wider text-[#14E0BF] hover:text-white transition-colors flex items-center gap-2"
          >
            See the launch process <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default VirtualBrandsBento;
